import React, { useState } from "react";
import { personalInfo } from "../data/content";

const initialForm = { name: "", email: "", message: "", company: "" };

export default function Contact() {
  const [formData, setFormData] = useState(initialForm);
  const [submitState, setSubmitState] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = "Name is required.";
    if (!formData.email.trim()) {
      next.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      next.email = "Enter a valid email address.";
    }
    if (!formData.message.trim()) next.message = "Message is required.";
    else if (formData.message.trim().length < 10) {
      next.message = "Message should be at least 10 characters.";
    }
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.company) {
      setSubmitState("success");
      setSubmitMessage("Message sent. Thank you.");
      setFormData(initialForm);
      return;
    }

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitState("error");
      setSubmitMessage("Please fix the highlighted fields.");
      return;
    }

    setErrors({});
    setSubmitState("sending");
    setSubmitMessage("");

    try {
      const response = await fetch(
        `https://formsubmit.co/ajax/${personalInfo.email}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            email: formData.email.trim(),
            message: formData.message.trim(),
            _subject: `Portfolio Contact from ${formData.name.trim()}`,
            _template: "table",
            _captcha: "false",
            _honey: "",
          }),
        }
      );

      const result = await response.json().catch(() => null);

      if (
        !response.ok ||
        result?.success === "false" ||
        result?.success === false
      ) {
        throw new Error(result?.message || "Unable to send your message right now.");
      }

      setSubmitState("success");
      setSubmitMessage(
        "Message sent. Thank you — I’ll get back to you as soon as I can."
      );
      setFormData(initialForm);
    } catch (error) {
      setSubmitState("error");
      setSubmitMessage("Message could not be sent. Please email me directly instead.");
    }
  };

  return (
    <section id="contact" className="section-pad border-t border-rule bg-canvas-raised">
      <div className="wrap">
        <header className="reveal mb-12 max-w-3xl">
          <p className="label">09 / Contact</p>
          <h2 className="mt-4 font-display text-[clamp(1.75rem,3vw,2.5rem)] font-semibold tracking-[-0.02em]">
            Start a conversation
          </h2>
          <p className="mt-4 text-ink-secondary">
            Roles, consulting, freelance, or a technical discussion about a
            product or system. Prefer email? Reach me directly below.
          </p>
        </header>

        <div className="grid gap-12 lg:grid-cols-12">
          <div className="reveal space-y-8 lg:col-span-5">
            <div>
              <h3 className="label">Email</h3>
              <a
                href={`mailto:${personalInfo.email}`}
                className="mt-2 inline-block text-ink underline decoration-accent underline-offset-4 hover:text-accent-strong"
              >
                {personalInfo.email}
              </a>
            </div>
            <div>
              <h3 className="label">Location</h3>
              <p className="mt-2 text-ink">{personalInfo.location}</p>
            </div>
            <div>
              <h3 className="label">Profiles</h3>
              <div className="mt-3 flex flex-wrap gap-3">
                <a
                  href={personalInfo.github}
                  className="btn-secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
                <a
                  href={personalInfo.linkedin}
                  className="btn-secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
                <a
                  href={personalInfo.resumeUrl}
                  className="btn-secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Resume
                </a>
              </div>
            </div>
          </div>

          <div className="reveal lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              noValidate
              className="border border-rule bg-canvas p-6 md:p-8"
              aria-describedby="form-status"
            >
              <div className="mb-4 hidden" aria-hidden="true">
                <label htmlFor="company">Company</label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-5">
                <div>
                  <label htmlFor="name" className="label mb-2 block">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? "name-error" : undefined}
                    className="w-full border border-rule bg-canvas-raised px-4 py-3 text-ink placeholder:text-ink-muted/70 focus:border-accent focus:outline-none"
                    placeholder="Your name"
                  />
                  {errors.name ? (
                    <p id="name-error" className="mt-2 text-sm text-danger">
                      {errors.name}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="email" className="label mb-2 block">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    className="w-full border border-rule bg-canvas-raised px-4 py-3 text-ink placeholder:text-ink-muted/70 focus:border-accent focus:outline-none"
                    placeholder="you@company.com"
                  />
                  {errors.email ? (
                    <p id="email-error" className="mt-2 text-sm text-danger">
                      {errors.email}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="message" className="label mb-2 block">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={
                      errors.message ? "message-error" : undefined
                    }
                    className="w-full resize-y border border-rule bg-canvas-raised px-4 py-3 text-ink placeholder:text-ink-muted/70 focus:border-accent focus:outline-none"
                    placeholder="What are you building or trying to solve?"
                  />
                  {errors.message ? (
                    <p id="message-error" className="mt-2 text-sm text-danger">
                      {errors.message}
                    </p>
                  ) : null}
                </div>

                <button
                  type="submit"
                  disabled={submitState === "sending"}
                  className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitState === "sending" ? "Sending…" : "Send message"}
                </button>

                <div id="form-status" role="status" aria-live="polite">
                  {submitMessage ? (
                    <p
                      className={`text-sm ${
                        submitState === "success" ? "text-accent-strong" : "text-danger"
                      }`}
                    >
                      {submitMessage}
                      {submitState === "error" ? (
                        <>
                          {" "}
                          <a
                            href={`mailto:${personalInfo.email}`}
                            className="underline underline-offset-2"
                          >
                            {personalInfo.email}
                          </a>
                        </>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
